import type {
  AccessGrant,
  AccessPrincipalType,
  AccessResourceType,
  AccessRole,
} from '~/features/access-control/types/AccessControl';

import type { ResourceAccess } from './accessGrantMapper';

type ClauseLibraryAccessResourceType = Extract<
  AccessResourceType,
  'clause' | 'clause_variant' | 'clause_root'
>;

type ClauseAccessLevel = Extract<AccessRole, 'view' | 'edit'>;

type ClauseAccessProvenance = 'direct' | 'inherited';

type ServerClauseAccessGrant = {
  principal_type: AccessPrincipalType;
  principal_id: string;
  access: ClauseAccessLevel;
  provenance: ClauseAccessProvenance;
};

type ServerClauseAccessAttributes = {
  grants: ServerClauseAccessGrant[];
  inherit_from_parent?: boolean;
};

type ServerClauseAccessDataType =
  | 'clause_library_access'
  | 'clause_access'
  | 'clause_variation_access';

export type ServerClauseAccessResponse = {
  data: {
    type: ServerClauseAccessDataType;
    id: string;
    attributes: ServerClauseAccessAttributes;
  };
};

type ServerClauseAccessRequestData =
  | {
      type: 'clause_library_access';
      attributes: { grants: ServerClauseAccessGrant[] };
    }
  | {
      type: 'clause_access' | 'clause_variation_access';
      attributes: {
        grants: ServerClauseAccessGrant[];
        inherit_from_parent: boolean;
      };
    };

export type ServerClauseAccessRequest = {
  data: ServerClauseAccessRequestData;
};

const ACCESS_PATH_BY_TYPE: Record<
  ClauseLibraryAccessResourceType,
  (resourceId: string) => string
> = {
  clause_root: () => '/library/access',
  clause: (resourceId) => `/clauses/${resourceId}/access`,
  clause_variant: (resourceId) => `/variations/${resourceId}/access`,
};

const DATA_TYPE_BY_RESOURCE = {
  clause_root: 'clause_library_access',
  clause: 'clause_access',
  clause_variant: 'clause_variation_access',
} as const satisfies Record<
  ClauseLibraryAccessResourceType,
  ServerClauseAccessDataType
>;

export function isClauseLibraryAccessResourceType(
  resourceType: AccessResourceType,
): resourceType is ClauseLibraryAccessResourceType {
  return (
    resourceType === 'clause' ||
    resourceType === 'clause_variant' ||
    resourceType === 'clause_root'
  );
}

export function getClauseAccessPath(
  resourceType: ClauseLibraryAccessResourceType,
  resourceId: string,
) {
  return ACCESS_PATH_BY_TYPE[resourceType](resourceId);
}

function mapServerGrant(grant: ServerClauseAccessGrant): AccessGrant {
  return {
    principal: {
      id: grant.principal_id,
      name: '',
      type: grant.principal_type,
    },
    role: grant.access,
    source: grant.provenance,
  };
}

export function mapClauseAccessResponse(
  serverResponse: ServerClauseAccessResponse,
): ResourceAccess {
  const { attributes } = serverResponse.data;

  return {
    grants: attributes.grants.map(mapServerGrant),
    inheritFromParent: attributes.inherit_from_parent ?? false,
  };
}

function mapGrantToServer(grant: AccessGrant): ServerClauseAccessGrant {
  return {
    access: grant.role as ClauseAccessLevel,
    principal_id: grant.principal.id,
    principal_type: grant.principal.type,
    provenance: 'direct',
  };
}

function selectGrantsForRequest(
  grants: AccessGrant[],
  inheritFromParent: boolean,
) {
  if (!inheritFromParent) {
    return grants;
  }

  return grants.filter((grant) => grant.source === 'direct');
}

export function mapClauseAccessRequest({
  grants,
  inheritFromParent,
  resourceType,
}: {
  grants: AccessGrant[];
  inheritFromParent: boolean;
  resourceType: ClauseLibraryAccessResourceType;
}): ServerClauseAccessRequest {
  const requestGrants = selectGrantsForRequest(grants, inheritFromParent).map(
    mapGrantToServer,
  );

  if (resourceType === 'clause_root') {
    return {
      data: {
        type: DATA_TYPE_BY_RESOURCE.clause_root,
        attributes: { grants: requestGrants },
      },
    };
  }

  return {
    data: {
      type: DATA_TYPE_BY_RESOURCE[resourceType],
      attributes: {
        grants: requestGrants,
        inherit_from_parent: inheritFromParent,
      },
    },
  };
}
